import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { format, isValid, parse } from "date-fns";
import {
  getOrganizationCustomer,
  listPartnerPrograms,
} from "../../../helpers/partnerCentral_helper";
import { formFromOrganizationCustomer } from "../../PartnerCentral/customerPayload";
import { programOptionLabel } from "../../PartnerCentral/WholesaleProgramsField";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
} from "../../Compliance/AuditHub/theme";
import {
  getCustomerAccountEntries,
  getCustomerAccountNumbers,
  getUniqueCustomerPayers,
  isCustomerActive,
  isSubOrganization,
} from "../utils/customerHelpers";

const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const iso = raw.slice(0, 10);
  const parsed = parse(iso, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return format(parsed, "MM/dd/yyyy");
  const local = new Date(raw.replace(/-/g, "/"));
  if (!Number.isNaN(local.getTime())) {
    return local.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return raw || "—";
};

const display = (value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const resolveOrganizationId = (customer) => {
  const raw =
    customer?.organization_id ?? customer?.organizationId ?? customer?.orgId;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
};

/** Read-only creator email/name from org or Partner Central audit fields. */
const resolveCreatedBy = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const value =
      source.createdBy ??
      source.created_by ??
      source.createdby ??
      source.created_by_username ??
      source.createdByUsername ??
      source.created_by_email ??
      source.createdByEmail;
    const text = value == null ? "" : String(value).trim();
    if (text) return text;
  }
  return "";
};

const resolveCreatedDate = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const value =
      source.createdDate ??
      source.created_date ??
      source.created_at ??
      source.createdAt;
    if (value) return value;
  }
  return "";
};

const StatusChip = ({ status }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return (
    <Chip
      size="small"
      label={status || "—"}
      sx={{
        height: 22,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: tone.bg,
        color: tone.fg,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

StatusChip.propTypes = {
  status: PropTypes.string,
};

/** Same visual language as Selected Accounts: payer chips labeled + accent-highlighted. */
const AccountChips = ({ items }) => {
  if (!items?.length) {
    return (
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: AH_TEXT }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {items.map((item) => {
        const number = item.accountNumber;
        const isPayer = Boolean(item.isPayer);
        return (
          <Chip
            key={`${isPayer ? "payer" : "payee"}-${number}`}
            size="small"
            label={isPayer ? `Payer: ${number}` : number}
            title={isPayer ? `Payer: ${number}` : number}
            sx={{
              height: 24,
              fontSize: 12,
              fontFamily: "monospace",
              fontWeight: 500,
              borderRadius: "6px",
              backgroundColor: isPayer ? "#fff4eb" : AH_TRACK,
              color: AH_TEXT,
              border: `1px solid ${isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
              maxWidth: "100%",
              "& .MuiChip-label": {
                px: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        );
      })}
    </Box>
  );
};

AccountChips.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      accountNumber: PropTypes.string.isRequired,
      isPayer: PropTypes.bool,
    })
  ),
};

/** Linked account chips with payer ids first (mirrors Selected Accounts order). */
const buildLinkedAccountItems = (customer, form, accountNumbers) => {
  const numbers = [
    ...new Set(
      (accountNumbers || [])
        .map((id) => String(id || "").trim())
        .filter((id) => /^\d{12}$/.test(id) || Boolean(id))
    ),
  ];
  if (!numbers.length) return [];

  const payerSet = new Set();
  (form?.payerAccountNumbers || []).forEach((id) => {
    const n = String(id || "").trim();
    if (n && numbers.includes(n)) payerSet.add(n);
  });
  getUniqueCustomerPayers(customer).forEach((payer) => {
    if (payer?.id && numbers.includes(payer.id)) payerSet.add(payer.id);
  });
  getCustomerAccountEntries(customer).forEach((acc) => {
    const number = String(acc?.accountNumber || "").trim();
    const payerId = String(acc?.payerAccountId || "").trim();
    if (number && payerId && number === payerId && numbers.includes(number)) {
      payerSet.add(number);
    }
  });

  const payers = numbers.filter((id) => payerSet.has(id));
  const payees = numbers.filter((id) => !payerSet.has(id));
  return [
    ...payers.map((accountNumber) => ({ accountNumber, isPayer: true })),
    ...payees.map((accountNumber) => ({ accountNumber, isPayer: false })),
  ];
};

const DetailRow = ({ label, value, last }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 38%) 1fr" },
      gap: { xs: 0.35, sm: 2 },
      alignItems: "start",
      py: 1.25,
      borderBottom: last ? "none" : `1px solid ${AH_BORDER}`,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: AH_MUTED,
        lineHeight: 1.45,
        pt: { sm: 0.1 },
      }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: AH_TEXT,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  last: PropTypes.bool,
};

const DetailSection = ({ title, rows }) => {
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        border: `1px solid ${AH_BORDER}`,
        borderRadius: "10px",
        backgroundColor: AH_WHITE,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1,
          backgroundColor: AH_TRACK,
          borderBottom: `1px solid ${AH_BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: AH_TEXT,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 1.75 }}>
        {rows.map((row, index) => (
          <DetailRow
            key={row.label}
            label={row.label}
            value={row.value}
            last={index === rows.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
    })
  ).isRequired,
};

const pushIf = (rows, condition, item) => {
  if (condition) rows.push(item);
};

const wholesaleProgramLabel = (ref, programLookup) => {
  const type = String(ref?.program_type || "").trim();
  const id = String(ref?.program_id || "").trim();
  if (!type || !id) return "—";
  const program = programLookup?.get(`${type}:${id}`);
  if (program) return programOptionLabel(program);
  // Prefer any name fields the API may already include on the ref.
  const name =
    ref.program_display_name ||
    ref.programDisplayName ||
    ref.display_name ||
    ref.displayName ||
    ref.agreement_id ||
    ref.agreementId;
  if (name) {
    return programOptionLabel({
      program_type: type,
      program_display_name: name,
      id,
    });
  }
  return `${type} · ${id}`;
};

const buildSections = (customer, form, programLookup = new Map()) => {
  const accounts =
    form?.payeeAccountNumbers?.length
      ? form.payeeAccountNumbers
      : getCustomerAccountNumbers(customer);
  const active = form?.active != null ? Boolean(form.active) : isCustomerActive(customer);
  const statusLabel =
    (typeof form?.status === "string" && form.status) ||
    (active ? "ACTIVE" : "INACTIVE");

  const overview = [
    {
      label: "Organization name",
      value: display(form?.organizationName || customer?.customer_name),
    },
  ];
  pushIf(overview, Boolean(form?.customerId), {
    label: "Customer ID",
    value: display(form.customerId),
  });
  const createdDate = resolveCreatedDate(form, customer);
  const createdBy = resolveCreatedBy(form, customer);
  overview.push({
    label: "Created",
    value: formatDate(createdDate),
  });
  pushIf(overview, Boolean(createdBy), {
    label: "Created by",
    value: display(createdBy),
  });
  overview.push({
    label: "Status",
    value: <StatusChip status={String(statusLabel).toUpperCase()} />,
  });
  pushIf(overview, Boolean(form?.orgDescription || customer?.orgdescription), {
    label: "Description",
    value: display(form?.orgDescription || customer?.orgdescription),
  });
  pushIf(overview, isSubOrganization(customer), {
    label: "Type",
    value: "Sub-organization",
  });

  const contact = [
    {
      label: "Primary contact",
      value: display(
        form?.primaryContactName ||
          customer?.orgprimaryname ||
          customer?.orgPrimaryName
      ),
    },
    {
      label: "Email",
      value: display(
        form?.primaryContactEmail ||
          customer?.orgprimaryemail ||
          customer?.orgPrimaryEmail
      ),
    },
    {
      label: "Phone",
      value: display(
        form?.phoneNumber || customer?.phonenumber || customer?.phoneNumber
      ),
    },
  ];

  const addressLines = [
    form?.orgAddress || customer?.orgaddress,
    [form?.orgCity || customer?.orgcity, form?.orgState || customer?.orgstate]
      .filter(Boolean)
      .join(", "),
    [form?.orgCountry || customer?.orgcountry, form?.orgZipcode || customer?.orgzip]
      .filter(Boolean)
      .join(" "),
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  const address = [
    {
      label: "Address",
      value: addressLines.length ? addressLines.join(", ") : "—",
    },
  ];

  const billingParts = [
    form?.orgBillingName,
    form?.orgBillingEmail,
    form?.orgBillingAddress,
    [form?.orgBillingCity, form?.orgBillingState].filter(Boolean).join(", "),
    [form?.orgBillingCountry, form?.orgBillingZip].filter(Boolean).join(" "),
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  const billing = [];
  pushIf(billing, billingParts.length > 0, {
    label: "Billing contact",
    value: billingParts.join(", "),
  });

  const linkedItems = buildLinkedAccountItems(customer, form, accounts);
  const accountsSection = [
    {
      label: linkedItems.length
        ? `Linked accounts (${linkedItems.length})`
        : "Linked accounts",
      value: <AccountChips items={linkedItems} />,
    },
  ];

  const commercial = [];
  pushIf(commercial, Boolean(form?.customerId), {
    label: "Customer code",
    value: display(form.customerId),
  });
  pushIf(commercial, Boolean(form?.startDate || form?.endDate), {
    label: "Contract term",
    value:
      form?.startDate || form?.endDate
        ? `${formatDate(form.startDate)} – ${formatDate(form.endDate)}`
        : "—",
  });
  pushIf(commercial, Boolean(form?.paymentTerms), {
    label: "Payment terms",
    value: display(form.paymentTerms),
  });
  pushIf(commercial, Boolean(form?.multiYearCommitment), {
    label: "Multi-year commitment",
    value: form.multiYearCommitment ? `$${form.multiYearCommitment}` : "—",
  });
  pushIf(commercial, Boolean(form?.commitmentDiscount), {
    label: "Discount",
    value: form.commitmentDiscount ? `${form.commitmentDiscount}%` : "—",
  });
  pushIf(commercial, Boolean(form?.pricingModel), {
    label: "Pricing model",
    value: display(form.pricingModel),
  });
  pushIf(commercial, Boolean(form?.regions?.length), {
    label: "Operating regions",
    value: (form?.regions || []).join(", "),
  });
  pushIf(commercial, Boolean(form?.serviceExclusions?.length), {
    label: "Service exclusions",
    value: (form?.serviceExclusions || []).join(", "),
  });
  pushIf(commercial, Boolean(form?.wholesalePrograms?.length), {
    label: "Wholesale programs",
    value: (form?.wholesalePrograms || [])
      .map((ref) => wholesaleProgramLabel(ref, programLookup))
      .join("; "),
  });
  pushIf(
    commercial,
    form?.commitmentMode === "annual" && Boolean(form?.years?.length),
    {
      label: "Annual commitment years",
      value: display(form?.years?.length),
    }
  );
  pushIf(
    commercial,
    form?.commitmentMode === "tiers" && Boolean(form?.tiers?.length),
    {
      label: "Volume discount tiers",
      value: display(form?.tiers?.length),
    }
  );

  return [
    { title: "Overview", rows: overview },
    { title: "Contact", rows: contact },
    { title: "Address", rows: address },
    { title: "Billing", rows: billing },
    { title: "Accounts", rows: accountsSection },
    { title: "Commercial terms", rows: commercial },
  ].filter((section) => section.rows.length > 0);
};

const baselineFormFromCustomer = (customer) => {
  if (!customer) return null;
  return {
    organizationName: customer.customer_name || "",
    customerOrgcode: customer.orgcode || "",
    organizationId: resolveOrganizationId(customer),
    primaryContactName: customer.orgprimaryname || customer.orgPrimaryName || "",
    primaryContactEmail: customer.orgprimaryemail || customer.orgPrimaryEmail || "",
    phoneNumber: customer.phonenumber || customer.phoneNumber || "",
    orgDescription: customer.orgdescription || customer.orgDescription || "",
    orgAddress: customer.orgaddress || customer.orgAddress || "",
    orgCity: customer.orgcity || customer.orgCity || "",
    orgState: customer.orgstate || customer.orgState || "",
    orgZipcode: customer.orgzip || customer.orgZip || "",
    orgCountry: customer.orgcountry || customer.orgCountry || "",
    payeeAccountNumbers: getCustomerAccountNumbers(customer),
    active: isCustomerActive(customer),
    createdBy: resolveCreatedBy(customer),
    createdDate: resolveCreatedDate(customer),
  };
};

const CustomerDetailsDialog = ({ open, onClose, customer }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [programLookup, setProgramLookup] = useState(() => new Map());

  const organizationId = resolveOrganizationId(customer);

  useEffect(() => {
    if (!open || !customer) {
      setDetail(null);
      setError("");
      setLoading(false);
      setProgramLookup(new Map());
      return undefined;
    }

    if (organizationId == null) {
      setDetail(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);
    getOrganizationCustomer(organizationId)
      .then((payload) => {
        if (cancelled) return;
        const record =
          payload?.organization_id != null || payload?.orgcode || payload?.partnerCentral
            ? payload
            : payload?.data && typeof payload.data === "object"
              ? payload.data
              : null;
        if (!record) {
          setError("Unable to load customer details.");
          return;
        }
        const mapped = formFromOrganizationCustomer(record);
        const pc =
          record.partnerCentral && typeof record.partnerCentral === "object"
            ? record.partnerCentral
            : {};
        setDetail({
          ...mapped,
          status:
            pc.status ||
            (record.active === false ? "INACTIVE" : "ACTIVE"),
          createdBy: resolveCreatedBy(mapped, record, pc),
          createdDate: resolveCreatedDate(mapped, record, pc),
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Unable to load customer details.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, customer, organizationId]);

  // Resolve wholesale program UUIDs → MSP-entered display names (same source as Add Customer).
  useEffect(() => {
    if (!open) {
      setProgramLookup(new Map());
      return undefined;
    }
    let cancelled = false;
    listPartnerPrograms({ statuses: ["DRAFT", "ACTIVE"] })
      .then((programs) => {
        if (cancelled) return;
        const next = new Map();
        (Array.isArray(programs) ? programs : []).forEach((program) => {
          const type = String(program?.program_type || "").trim();
          const id = String(program?.id || "").trim();
          if (type && id) next.set(`${type}:${id}`, program);
        });
        setProgramLookup(next);
      })
      .catch(() => {
        if (!cancelled) setProgramLookup(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const form = useMemo(() => {
    if (detail) return detail;
    return baselineFormFromCustomer(customer);
  }, [detail, customer]);

  const sections = useMemo(() => {
    if (!customer || !form) return [];
    return buildSections(customer, form, programLookup);
  }, [customer, form, programLookup]);

  const title =
    form?.organizationName || customer?.customer_name || customer?.orgcode || "Customer";
  const subtitleParts = [
    form?.customerId ? `ID ${form.customerId}` : null,
  ].filter(Boolean);

  return (
    <Dialog
      open={open && Boolean(customer)}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="customer-details-dialog-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 680,
          maxHeight: { xs: "92vh", sm: "88vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          flexShrink: 0,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="customer-details-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
            }}
          >
            Customer Details
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: AH_WHITE,
              opacity: 0.92,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
            {subtitleParts.length ? ` · ${subtitleParts.join(" · ")}` : ""}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={loading && !form}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: loading && !form ? "default" : "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.5,
          backgroundColor: AH_WHITE,
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {loading && !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: AH_ACCENT }} />
          </Box>
        ) : error && !form ? (
          <Typography sx={{ fontSize: 13, color: "#d32f2f" }} role="alert">
            {error}
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1.75 }}>
            {error ? (
              <Typography sx={{ fontSize: 12, color: "#d32f2f" }} role="alert">
                {error} Showing available summary details.
              </Typography>
            ) : null}
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: AH_MUTED }} />
                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                  Loading full customer details…
                </Typography>
              </Box>
            ) : null}
            {sections.map((section) => (
              <DetailSection
                key={section.title}
                title={section.title}
                rows={section.rows}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={loading && !form}
          sx={{
            ...outlinedButtonSx,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": {
              backgroundColor: "#686c7f",
              borderColor: "#686c7f",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

CustomerDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerDetailsDialog;
